import { Fragment } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RecipeComponent } from '../types';

interface RecipeBankIngredientsProps {
  components: RecipeComponent[];
}

const IngredientRows = ({ components, depth = 0 }: { components: RecipeComponent[]; depth?: number }) => (
  <>
    {components.map((component) => (
      <Fragment key={`${component.id}-${depth}`}>
        <TableRow>
          <TableCell sx={{ pl: 2.5 + depth * 3 }}>
            {component.componentDescription}
            {component.isRecipe && (
              <Typography component="span"
                variant="textXS"
                color="text.secondary"
                sx={{ ml: 1 }}>(recipe)</Typography>
            )}
          </TableCell>
          <TableCell>{component.componentItemNumber}</TableCell>
          <TableCell align="right">{component.quantity}</TableCell>
          <TableCell align="right">{component.weight}</TableCell>
          <TableCell align="right">{component.volume}</TableCell>
          <TableCell>{component.uom}</TableCell>
        </TableRow>
        {component.children?.length ? <IngredientRows components={component.children}
          depth={depth + 1} /> : null}
      </Fragment>
    ))}
  </>
);

/** Read-only ingredients/components view - no pricing, supplier data or edit affordances. */
const RecipeBankIngredientsTab = ({ components }: RecipeBankIngredientsProps) => {
  const { t } = useTranslation('recipeV2');

  if (!components.length) {
    return <Typography color="text.secondary">{t('recipeBank.detail.noIngredients', 'No ingredients recorded for this recipe.')}</Typography>;
  }

  return (
    <Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('ingredient')}</TableCell>
            <TableCell>{t('sku')}</TableCell>
            <TableCell align="right">{t('quantity')}</TableCell>
            <TableCell align="right">{t('build.weight', 'Weight')}</TableCell>
            <TableCell align="right">{t('build.volume', 'Volume')}</TableCell>
            <TableCell>{t('build.uom', 'UOM')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <IngredientRows components={components} />
        </TableBody>
      </Table>
    </Box>
  );
};

export default RecipeBankIngredientsTab;
